import React from 'react';
import { match } from 'ts-pattern';

import { useTaskProcessor } from '@eluve/appointment-tasks';
import { Button, LoadingIcon, Stack } from '@eluve/components';
import { useIsSummarized } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { FeatureFlaggedComponent } from '@eluve/smart-blocks';
import { useRecordingPauseCooldown } from '@eluve/stores';

import { useAppointmentDialogs } from '../AppointmentDialogProvider';
import { useIsRecordingTooShortForNote } from '../machines/transcription/hooks/useRecordingDurationGuard';
import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../machines/transcription/hooks/useTranscriptionMachine';

import { ShortRecordingNoteGuardTooltip } from './ShortRecordingNoteGuardTooltip';
import { UploadAudioFileButton } from './UploadAudioFileButton';

interface AppointmentTranscriptionInSessionControlsProps {
  onEndSession?: () => void;
}

export const AppointmentTranscriptionInSessionControls: React.FC<
  AppointmentTranscriptionInSessionControlsProps
> = ({ onEndSession }) => {
  const { t } = useTranslation();
  const logger = useNamedLogger('AppointmentTranscriptionInSessionControls');
  const isSummarized = useIsSummarized();
  const appointmentId = useTranscriptionMachineSelector(
    (state) => state.context.sessionAppointmentId ?? '',
  );

  const { openOverwriteSummaryDialog } = useAppointmentDialogs();

  const { taskProcessor } = useTaskProcessor();

  const captureEvent = useCaptureEvent({
    appointmentId,
  });

  const recordingStartedAt = useTranscriptionMachineSelector((state) =>
    state.matches({ InSession: 'Recording' })
      ? state.context.recordingStartedAt
      : undefined,
  );
  const isPauseCooldownActive = useRecordingPauseCooldown(recordingStartedAt);

  const state = useTranscriptionMachineSelector((state) => {
    if (
      state.matches({
        InSession: {
          Recording: {
            MediaRecorder: 'Loading',
          },
        },
      })
    ) {
      return 'Loading';
    }

    if (
      state.matches({
        InSession: { Recording: { FinalizingRecording: 'Finalizing' } },
      })
    ) {
      if (state.context.isPausing) {
        return 'FinalizingPause';
      }

      return 'FinalizingStop';
    }

    if (
      state.matches({
        InSession: 'Recording',
      })
    ) {
      return 'Recording';
    }

    if (
      state.matches({
        InSession: 'Paused',
      })
    ) {
      return 'Paused';
    }
  });

  const { send } = useTranscriptionMachineActor();

  const isTooShort = useIsRecordingTooShortForNote();

  const endSession = () => {
    // Guarded by the disabled button; defensive against programmatic calls.
    if (isTooShort) {
      return;
    }
    logger.info('Generate Note clicked', { appointmentId });
    captureEvent('ended_session');
    send({ type: 'STOP' });
    onEndSession?.();

    if (isSummarized) {
      openOverwriteSummaryDialog(appointmentId);
    } else {
      taskProcessor.addTask({
        type: 'TASK.GENERATE_NOTE',
        appointmentId,
        userCompletedAt: new Date().toISOString(),
      });
    }
  };

  return match(state)
    .with('Recording', 'FinalizingPause', 'FinalizingStop', () => (
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <Button
          variant="secondary"
          onClick={() => {
            logger.info('Pause clicked', { appointmentId });
            captureEvent('paused_session');
            send({ type: 'PAUSE' });
          }}
          disabled={state === 'FinalizingStop' || isPauseCooldownActive}
          loading={state === 'FinalizingPause'}
        >
          {t('appointmentPage.controls.pause')}
        </Button>

        <ShortRecordingNoteGuardTooltip active={isTooShort}>
          <Button
            data-accessibility-label="generate-note-button"
            disabled={state === 'FinalizingPause' || isTooShort}
            loading={state === 'FinalizingStop'}
            onClick={endSession}
          >
            {t('appointmentPage.controls.generateNote')}
          </Button>
        </ShortRecordingNoteGuardTooltip>
      </Stack>
    ))
    .with('Paused', () => (
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <FeatureFlaggedComponent flag="ALLOW_AUDIO_FILE_UPLOAD_FOR_APPOINTMENT">
          <UploadAudioFileButton
            appointmentId={appointmentId}
            buttonSize="md"
          />
        </FeatureFlaggedComponent>
        <Button
          variant="secondary"
          onClick={() => {
            captureEvent('resumed_session');
            send({ type: 'RESUME' });
          }}
        >
          {t('appointmentPage.controls.resume')}
        </Button>
        <ShortRecordingNoteGuardTooltip active={isTooShort}>
          <Button
            data-accessibility-label="generate-note-button"
            disabled={isTooShort}
            onClick={endSession}
          >
            {t('appointmentPage.controls.generateNote')}
          </Button>
        </ShortRecordingNoteGuardTooltip>
      </Stack>
    ))
    .with('Loading', () => (
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <LoadingIcon size="lg" className="mr-2 text-bg-accent-strong-primary" />
        {t('appointmentPage.controls.processing')}
      </Stack>
    ))
    .otherwise(() => null);
};
